import { Base } from '@studiometa/js-toolkit';
import { Accordion, Fetch } from '@studiometa/ui';

/**
 * VariantRadios component with Fetch integration
 * Uses @studiometa/ui Fetch component for automatic DOM updates
 */
export default class VariantRadios extends Base {
  /**
   * Component Configuration
   */
  static config = {
    name: 'VariantRadios',
    emits: ['variant-changed'],
    refs: ['input[]', 'fetchLink'],
    components: {
      Accordion,
      Fetch,
    },
    options: {
      url: {
        type: String,
        required: true,
        default: '',
      },
      section: {
        type: String,
        default: '',
      },
    },
  };

  /**
   * Mounted hook
   */
  mounted() {
    this._variantData = JSON.parse(this.$el.querySelector('[type="application/json"]').textContent);
    this._useOptionValues = false;
  }

  /**
   * Update refs after fetch replaces DOM elements.
   * When option_values were used, clean up history URL with resolved variant ID.
   */
  onFetchLinkFetchUpdateAfter() {
    this.$parent.$update();

    if (this._useOptionValues) {
      this._useOptionValues = false;
      const variantInput = document.querySelector(`#variant-input-${this.$options.section}`);
      if (variantInput?.value) {
        history.replaceState(
          {},
          document.title,
          `${this.$options.url}?variant=${variantInput.value}`,
        );
      }
    }
  }

  /**
   * Get checked inputs
   * @returns {HTMLInputElement[]}
   */
  get checkedInputs() {
    return Array.from(this.$refs.input).filter((input) => input.checked);
  }

  /**
   * Get selected option value IDs (Shopify product_option_value.id).
   * Returns null if any ID is missing (store doesn't support the feature).
   * @returns {string[]|null}
   */
  get selectedOptionValueIds() {
    const checked = this.checkedInputs;
    const ids = checked.map((input) => input.dataset.optionValueId).filter(Boolean);
    return ids.length === checked.length ? ids : null;
  }

  /**
   * Get options
   * @returns {Array}
   */
  get options() {
    return this.checkedInputs.map((input) => input.value);
  }

  /**
   * Get the current variant
   * @returns {object|null}
   */
  get currentVariant() {
    return this.variantData.find((variant) => {
      return variant.options.every((option, index) => option === this.options[index]);
    });
  }

  /**
   * Get variant Data
   * @returns {Array}
   */
  get variantData() {
    return this._variantData;
  }

  /**
   * Handle input change - trigger fetch via hidden link.
   * Prefers option_values URL param (supports >250 variants) when Shopify option value IDs
   * are available, falls back to variant JSON lookup (limited to 250 variants).
   * @param {object} params Event params from js-toolkit
   * @param {HTMLInputElement} params.target The changed input element
   */
  onInputChange({ target } = {}) {
    const optionValueIds = this.selectedOptionValueIds;

    if (optionValueIds) {
      this._useOptionValues = true;
      const url = `${this.$options.url}?option_values=${optionValueIds.join(',')}&section_id=${this.$options.section}`;
      this.$refs.fetchLink.href = url;
      this.$refs.fetchLink.click();
      this.$emit('variant-changed', {}, { bubbles: true });
      return;
    }

    // Fallback: variant JSON lookup (limited to first 250 variants)
    let variant = this.currentVariant;

    if (!variant && target) {
      const changedName = target.name;
      const changedValue = target.value;
      const optionIndex = this.checkedInputs.findIndex((i) => i.name === changedName);

      if (optionIndex !== -1) {
        variant =
          this.variantData.find((v) => v.options[optionIndex] === changedValue && v.available) ??
          this.variantData.find((v) => v.options[optionIndex] === changedValue);
      }
    }

    if (!variant) return;

    const url = `${this.$options.url}?variant=${variant.id}`;
    this.$refs.fetchLink.href = url;
    this.$refs.fetchLink.click();
    this.$emit('variant-changed', { variant }, { bubbles: true });
  }
}
